// Map of Azad Jammu & Kashmir: the hospital network by tier, plus (on the dashboard) the kiosk program
// footprint per district. Leaflet on OpenStreetMap tiles; markers carry a surface ring, identity is
// given by colour + legend + tooltip, and a table view lists every facility.

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AJK_FACILITIES, DISTRICT_CENTRES, TIER_LABEL, type FacilityTier } from '@shared';

export interface DistrictStat {
  id: string;
  name: string;
  name_ur: string;
  facilities: number;
  kiosks: number;
  kiosks_online: number;
  screenings?: number;
  consults?: number;
  urgent?: number;
  open_flags?: number;
}

const TIERS: FacilityTier[] = ['teaching', 'district', 'tehsil'];
const RADIUS: Record<FacilityTier, number> = { teaching: 9, district: 7, tehsil: 5 };

function cssVar(el: Element, name: string) {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

export function AjkMap({ stats, windowDays, title = 'AJK health facility map' }: { stats?: DistrictStat[]; windowDays?: number; title?: string }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const programLayer = useRef<L.LayerGroup | null>(null);
  const [asTable, setAsTable] = useState(false);
  const [visible, setVisible] = useState<Record<FacilityTier, boolean>>({ teaching: true, district: true, tehsil: true });
  const tierLayers = useRef<Partial<Record<FacilityTier, L.LayerGroup>>>({});

  // Base map and facility markers (once).
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { scrollWheelZoom: false, zoomSnap: 0.25, attributionControl: true });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(m);
    const surface = cssVar(el.current, '--surface-1') || '#fcfcfb';
    programLayer.current = L.layerGroup().addTo(m);
    for (const tier of TIERS) {
      const color = cssVar(el.current, `--tier-${tier}`);
      const group = L.layerGroup();
      for (const fac of AJK_FACILITIES.filter((x) => x.tier === tier)) {
        L.circleMarker([fac.lat, fac.lng], { radius: RADIUS[tier], color: surface, weight: 2, fillColor: color, fillOpacity: 1 })
          .bindTooltip(`<strong>${fac.name}</strong><br><span lang="ur" dir="rtl">${fac.nameUr}</span><br>${TIER_LABEL[tier]} · ${fac.town}`, { direction: 'top', offset: [0, -RADIUS[tier]] })
          .addTo(group);
      }
      group.addTo(m);
      tierLayers.current[tier] = group;
    }
    m.fitBounds(L.latLngBounds(AJK_FACILITIES.map((x) => [x.lat, x.lng] as [number, number])).pad(0.05));
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  // Tier filter.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    for (const tier of TIERS) {
      const g = tierLayers.current[tier];
      if (!g) continue;
      if (visible[tier] && !m.hasLayer(g)) g.addTo(m);
      if (!visible[tier] && m.hasLayer(g)) m.removeLayer(g);
    }
  }, [visible]);

  // Program footprint: one bubble per district, area proportional to screenings.
  useEffect(() => {
    const layer = programLayer.current;
    if (!layer || !el.current) return;
    layer.clearLayers();
    if (!stats?.length) return;
    const ring = cssVar(el.current, '--text-muted') || '#898781';
    const max = Math.max(1, ...stats.map((s) => s.screenings ?? s.kiosks));
    for (const s of stats) {
      const c = DISTRICT_CENTRES[s.id];
      if (!c) continue;
      const value = s.screenings ?? s.kiosks;
      const radius = 14 + 26 * Math.sqrt(value / max);
      const offline = s.kiosks - s.kiosks_online;
      const lines = [
        `<strong>${s.name}</strong> <span lang="ur">${s.name_ur}</span>`,
        `${s.kiosks} pilot kiosk${s.kiosks === 1 ? '' : 's'} · ${s.kiosks_online} online${offline ? ` · ${offline} offline` : ''}`,
        s.screenings !== undefined ? `${s.screenings.toLocaleString()} screenings · ${s.consults ?? 0} teleconsults${windowDays ? ` (last ${windowDays} days)` : ''}` : '',
        s.urgent !== undefined ? `${s.urgent} urgent flags · ${s.open_flags ?? 0} open` : '',
      ].filter(Boolean);
      L.circleMarker([c.lat, c.lng], { radius, color: ring, weight: 1.5, fillColor: ring, fillOpacity: 0.1, interactive: true })
        .bindTooltip(lines.join('<br>'), { direction: 'right', offset: [radius, 0] })
        .addTo(layer);
      L.marker([c.lat, c.lng], {
        interactive: false,
        icon: L.divIcon({ className: 'map-district-label', html: `<span>${s.name.replace(' (Rawalakot)', '')}</span>`, iconSize: [0, 0] }),
      }).addTo(layer);
    }
  }, [stats, windowDays]);

  const counts = useMemo(() => Object.fromEntries(TIERS.map((t) => [t, AJK_FACILITIES.filter((x) => x.tier === t).length])) as Record<FacilityTier, number>, []);
  const byDistrict = useMemo(() => {
    const names: Record<string, string> = Object.fromEntries((stats ?? []).map((s) => [s.id, s.name]));
    return [...AJK_FACILITIES].sort((a, b) => a.district.localeCompare(b.district) || TIERS.indexOf(a.tier) - TIERS.indexOf(b.tier)).map((x) => ({ ...x, districtName: names[x.district] ?? DISTRICT_CENTRES[x.district]?.name ?? x.district }));
  }, [stats]);

  return (
    <section className="panel viz map-panel">
      <div className="viz-head">
        <div>
          <h3>{title}</h3>
          <div className="muted small">
            {AJK_FACILITIES.length} hospitals across all ten districts{stats?.length ? '; grey circles show the kiosk pilot in each district (size = screenings).' : '.'} Hover a marker for details.
          </div>
        </div>
        <button className="btn btn-small" onClick={() => setAsTable(!asTable)} aria-pressed={asTable}>{asTable ? 'Map view' : 'Table view'}</button>
      </div>
      <div className="legend map-legend" role="group" aria-label="Facility types">
        {TIERS.map((t) => (
          <label key={t} className="legend-item legend-toggle">
            <input type="checkbox" checked={visible[t]} onChange={(e) => setVisible({ ...visible, [t]: e.target.checked })} />
            <span className={`legend-dot tier-${t}`} />
            {TIER_LABEL[t]} ({counts[t]})
          </label>
        ))}
        {stats?.length ? <span className="legend-item"><span className="legend-dot program" />Kiosk pilot per district</span> : null}
      </div>
      <div className="map-layout" style={{ display: asTable ? 'none' : undefined }}>
        <div ref={el} className="ajk-map" />
        <ul className="map-districts" aria-label="Districts">
          {Object.entries(DISTRICT_CENTRES).map(([id, d]) => {
            const s = stats?.find((x) => x.id === id);
            const facs = AJK_FACILITIES.filter((x) => x.district === id);
            return (
              <li key={id}>
                <button className="map-district" onClick={() => map.current?.flyTo([d.lat, d.lng], 11, { duration: 0.8 })}>
                  <span className="md-name">{d.name} <span className="muted" lang="ur">{d.nameUr}</span></span>
                  <span className="md-facs">
                    {TIERS.map((t) => {
                      const n = facs.filter((x) => x.tier === t).length;
                      return n ? <span key={t} className="md-fac" title={TIER_LABEL[t]}><span className={`legend-dot tier-${t}`} />{n}</span> : null;
                    })}
                  </span>
                  {s && (
                    <span className="md-stats muted small">
                      {s.kiosks_online}/{s.kiosks} kiosks online{s.screenings !== undefined ? ` · ${s.screenings.toLocaleString()} screenings` : ''}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          <li>
            <button className="btn btn-small" onClick={() => map.current?.flyToBounds(L.latLngBounds(AJK_FACILITIES.map((x) => [x.lat, x.lng] as [number, number])).pad(0.05), { duration: 0.8 })}>
              Show all of AJK
            </button>
          </li>
        </ul>
      </div>
      {asTable && (
        <div className="viz-table">
          <table className="table">
            <thead><tr><th>District</th><th>Facility</th><th>Type</th><th>Town</th><th className="num">Lat, lng</th></tr></thead>
            <tbody>{byDistrict.map((x) => <tr key={x.id}><td>{x.districtName}</td><td>{x.name}</td><td>{TIER_LABEL[x.tier]}</td><td>{x.town}</td><td className="num">{x.lat.toFixed(3)}, {x.lng.toFixed(3)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Locations: district and tehsil headquarters towns from OpenStreetMap. The facility list is a public-source reference, to be reconciled with the
        Department&rsquo;s facility register (589 public facilities) at initiation; BHUs, RHCs and dispensaries are added from that register.
      </p>
    </section>
  );
}
